import { useState } from 'react';
import LessonCard from './components/LessonCard';
import { allLessons } from './data';
import { Link } from 'react-router-dom';
import SEOHead from '../../../components/SEOHead';

export default function Grade12Page() {
  const [activeTab, setActiveTab] = useState<'lessons' | 'worksheets' | 'standards'>('lessons');
  const navigate = window.REACT_APP_NAVIGATE;
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [showMembershipUpsell, setShowMembershipUpsell] = useState(false);
  const siteUrl = (import.meta.env.VITE_SITE_URL as string) || 'https://example.com';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Course',
    name: 'Grade 12 Precalculus | New York Mathematics | Augustine Anyimadu-Ahenkae',
    description: 'Complete Grade 12 Precalculus curriculum. Advanced functions, trigonometric identities, conic sections, sequences, vectors, and limits. Full AP Calculus readiness preparation.',
    url: `${siteUrl}/grades/grade-12`,
    provider: { '@type': 'Person', name: 'Augustine Anyimadu-Ahenkae', url: siteUrl },
    educationalLevel: 'Grade 12',
    inLanguage: 'en',
    hasCourseInstance: [
      { '@type': 'CourseInstance', name: 'Advanced Functions & Transformations', courseMode: 'online' },
      { '@type': 'CourseInstance', name: 'Trigonometric Functions & Identities', courseMode: 'online' },
      { '@type': 'CourseInstance', name: 'Conic Sections & Vectors', courseMode: 'online' },
      { '@type': 'CourseInstance', name: 'Sequences, Series & Limits', courseMode: 'online' },
    ],
  };

  const worksheets = [
    {
      id: 'precalc-1',
      title: 'Advanced Functions & Transformations',
      description: 'Master function composition, inverse functions, and graph transformations including stretches, compressions, and reflections',
      pages: 9,
      problems: 32,
      difficulty: 'Hard' as const,
      isFree: true
    },
    {
      id: 'precalc-2',
      title: 'Trigonometric Functions & Identities',
      description: 'Work with unit circle, prove trigonometric identities, and solve equations using sum/difference and double-angle formulas',
      pages: 10,
      problems: 35,
      difficulty: 'Hard' as const,
      isFree: false
    },
    {
      id: 'precalc-3',
      title: 'Polar Coordinates & Complex Numbers',
      description: 'Convert between rectangular and polar forms, graph polar equations, and apply De Moivre\'s theorem to complex numbers',
      pages: 8,
      problems: 28,
      difficulty: 'Hard' as const,
      isFree: false
    },
    {
      id: 'precalc-4',
      title: 'Vectors & Parametric Equations',
      description: 'Perform vector operations, find dot and cross products, and work with parametric and vector-valued functions',
      pages: 9,
      problems: 30,
      difficulty: 'Hard' as const,
      isFree: false
    },
    {
      id: 'precalc-5',
      title: 'Limits & Introduction to Calculus',
      description: 'Evaluate limits algebraically and graphically, understand continuity, and explore the concept of derivatives',
      pages: 10,
      problems: 33,
      difficulty: 'Hard' as const,
      isFree: false
    }
  ];

  const handleWorksheetClick = (worksheet: typeof worksheets[0]) => {
    if (worksheet.isFree) {
      alert('Free sample download started!');
    } else {
      setShowMembershipUpsell(true);
      setTimeout(() => {
        document.getElementById('membership-upsell')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    }
  };

  const handleBrowseAllClick = () => {
    window.REACT_APP_NAVIGATE('/worksheets?category=grade-12');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-teal-50/30">
      <SEOHead
        title="Grade 12 Precalculus New York | AP Calculus Readiness | Augustine Anyimadu-Ahenkae"
        description="Master Grade 12 Precalculus with advanced functions, trigonometric identities, conic sections, vectors, and limits. Includes NY Regents practice tests and full AP Calculus AB readiness preparation."
        canonical="/grades/grade-12"
        keywords="Grade 12 Precalculus, AP Calculus readiness, NY Regents Precalculus, trigonometric identities, conic sections, sequences series, 12th grade math"
        jsonLd={jsonLd}
      />
      {/* Navigation Bar */}
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/grades/hub')}
              className="flex items-center gap-2 text-slate-600 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-left-line text-xl"></i>
              <span className="font-medium">Back to Grades</span>
            </button>
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/grades/grade-9')}
                className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-all whitespace-nowrap cursor-pointer"
              >
                Grade 9
              </button>
              <button
                onClick={() => navigate('/grades/grade-10')}
                className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-all whitespace-nowrap cursor-pointer"
              >
                Grade 10
              </button>
              <button
                onClick={() => navigate('/grades/grade-11')}
                className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-all whitespace-nowrap cursor-pointer"
              >
                Grade 11
              </button>
              <div className="px-4 py-2 text-sm font-bold text-white bg-gradient-to-r from-teal-600 to-emerald-600 rounded-lg shadow-md whitespace-nowrap">
                Grade 12
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-teal-600 via-emerald-600 to-cyan-700 text-white py-20 overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-72 h-72 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-emerald-300 rounded-full blur-3xl"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
            <i className="ri-graduation-cap-line text-2xl"></i>
            <span className="font-semibold text-sm">High School Mathematics — Final Year</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            Grade 12: Precalculus
          </h1>
          <p className="text-xl text-teal-50 max-w-3xl mx-auto leading-relaxed">
            Master advanced functions, trigonometric identities, conic sections, sequences, vectors, and limits — the complete bridge to college-level calculus
          </p>
        </div>
      </div>

      {/* Practice Test Banners */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
        <Link
          to="/grades/precalc-tests"
          className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-teal-500 to-emerald-600 p-8 text-white shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
        >
          <div className="relative z-10">
            <div className="w-14 h-14 flex items-center justify-center bg-white/20 rounded-xl mb-4 group-hover:scale-110 transition-transform">
              <i className="ri-file-list-3-line text-3xl"></i>
            </div>
            <h3 className="text-xl font-bold mb-2">State Practice Tests</h3>
            <p className="text-teal-50 text-sm leading-relaxed">
              Full-length practice exams from NY, TX, OH, VA, and PA
            </p>
          </div>
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
        </Link>

        <Link
          to="/grades/precalc-regents-open"
          className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 p-8 text-white shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
        >
          <div className="relative z-10">
            <div className="w-14 h-14 flex items-center justify-center bg-white/20 rounded-xl mb-4 group-hover:scale-110 transition-transform">
              <i className="ri-edit-box-line text-3xl"></i>
            </div>
            <h3 className="text-xl font-bold mb-2">NY Regents Open Exam</h3>
            <p className="text-indigo-50 text-sm leading-relaxed">
              Parts I–IV with free-response questions and scoring rubrics
            </p>
          </div>
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
        </Link>

        <Link
          to="/grades/ap-calculus-readiness"
          className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-600 p-8 text-white shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
        >
          <div className="relative z-10">
            <div className="w-14 h-14 flex items-center justify-center bg-white/20 rounded-xl mb-4 group-hover:scale-110 transition-transform">
              <i className="ri-checkbox-circle-line text-3xl"></i>
            </div>
            <h3 className="text-xl font-bold mb-2">AP Calculus Readiness</h3>
            <p className="text-blue-50 text-sm leading-relaxed">
              30-question diagnostic quiz testing precalculus prerequisites
            </p>
          </div>
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
        </Link>

        <Link
          to="/grades/ap-calculus-ab-exam"
          className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-blue-700 to-indigo-800 p-8 text-white shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
        >
          <div className="relative z-10">
            <div className="w-14 h-14 flex items-center justify-center bg-white/20 rounded-xl mb-4 group-hover:scale-110 transition-transform">
              <i className="ri-timer-line text-3xl"></i>
            </div>
            <h3 className="text-xl font-bold mb-2">AP Calculus AB Mock Exam</h3>
            <p className="text-blue-50 text-sm leading-relaxed">
              45 MCQ + 6 FRQ timed mock exam — full AP Calculus AB simulation with scoring
            </p>
          </div>
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
        </Link>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-4 pb-16">
        {/* Worksheets Preview Banner */}
        <div className="mb-8 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-8 text-white shadow-xl">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex-1 min-w-[280px]">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                  <i className="ri-file-list-3-line text-2xl"></i>
                </div>
                <h2 className="text-2xl font-bold">Practice Worksheets Available</h2>
              </div>
              <p className="text-indigo-50 text-lg">
                Master Precalculus concepts with our advanced worksheet collection
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleBrowseAllClick}
                className="px-6 py-3 bg-white text-indigo-600 rounded-xl font-semibold hover:bg-indigo-50 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap flex items-center gap-2"
              >
                Browse All Grade 12 Worksheets
                <i className="ri-arrow-right-line"></i>
              </button>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex items-center gap-2 mb-8 bg-white rounded-xl p-2 shadow-sm border border-slate-200 w-fit">
          {(['lessons', 'worksheets', 'standards'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-6 py-3 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer capitalize ${
                activeTab === tab
                  ? 'bg-gradient-to-r from-teal-600 to-emerald-600 text-white shadow-md'
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              <i className={`mr-2 ${
                tab === 'lessons' ? 'ri-book-open-line' :
                tab === 'worksheets' ? 'ri-file-text-line' :
                'ri-checkbox-circle-line'
              }`}></i>
              {tab === 'lessons' ? 'Interactive Lessons' : tab === 'worksheets' ? 'Worksheets' : 'Standards Covered'}
            </button>
          ))}
        </div>

        {/* Lessons Tab */}
        {activeTab === 'lessons' && (
          <div>
            <div className="bg-gradient-to-r from-teal-50 to-emerald-50 rounded-2xl p-8 mb-10 border border-teal-100">
              <div className="flex items-start gap-6">
                <div className="w-16 h-16 bg-gradient-to-br from-teal-600 to-emerald-600 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg">
                  <i className="ri-user-smile-line text-3xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-slate-800 mb-3">
                    Welcome to Precalculus!
                  </h3>
                  <p className="text-slate-700 leading-relaxed mb-4">
                    This is the most important course before calculus. You'll master advanced functions, trigonometric identities, conic sections, sequences and series, vectors, parametric equations, and an introduction to limits. Every lesson includes detailed concept explanations, worked examples, and 30 practice questions with complete step-by-step solutions.
                  </p>
                  <p className="text-slate-500 text-sm italic">
                    — Mr. Augustine, Mathematics Department
                  </p>
                </div>
              </div>
            </div>

            {/* Lesson Count Summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
              {[
                { label: 'Total Lessons', value: allLessons.length, icon: 'ri-book-2-line', color: 'from-teal-500 to-emerald-500' },
                { label: 'Practice Questions', value: `${allLessons.length * 30}+`, icon: 'ri-question-line', color: 'from-emerald-500 to-cyan-500' },
                { label: 'State Tests', value: '5', icon: 'ri-map-pin-line', color: 'from-cyan-500 to-teal-600' },
                { label: 'Topics Covered', value: '8', icon: 'ri-list-check-2', color: 'from-teal-600 to-emerald-600' },
              ].map((stat, i) => (
                <div key={i} className="bg-white rounded-xl p-5 shadow-sm border border-slate-200 text-center">
                  <div className={`w-10 h-10 bg-gradient-to-br ${stat.color} rounded-lg flex items-center justify-center mx-auto mb-3`}>
                    <i className={`${stat.icon} text-xl text-white`}></i>
                  </div>
                  <div className="text-2xl font-bold text-slate-800">{stat.value}</div>
                  <div className="text-xs text-slate-500 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>

            <div className="space-y-6">
              {allLessons.map((lesson) => (
                <LessonCard key={lesson.id} lesson={lesson} />
              ))}
            </div>
          </div>
        )}

        {/* Worksheets Tab */}
        {activeTab === 'worksheets' && (
          <div className="space-y-8">
            {/* Free Sample Card */}
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-6 border-2 border-emerald-200 shadow-lg">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-[280px]">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="px-3 py-1 bg-emerald-500 text-white text-sm font-bold rounded-full">
                      FREE SAMPLE
                    </span>
                    <span className="px-3 py-1 bg-white text-emerald-700 text-sm font-semibold rounded-lg border border-emerald-200">
                      Hard
                    </span>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    {worksheets[0].title}
                  </h3>
                  <p className="text-gray-700 mb-4">
                    {worksheets[0].description}
                  </p>
                  <div className="flex items-center gap-6 text-sm text-gray-600">
                    <span className="flex items-center gap-2">
                      <i className="ri-file-text-line text-emerald-600"></i>
                      {worksheets[0].pages} pages
                    </span>
                    <span className="flex items-center gap-2">
                      <i className="ri-calculator-line text-emerald-600"></i>
                      {worksheets[0].problems} problems
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => handleWorksheetClick(worksheets[0])}
                  className="px-6 py-3 bg-emerald-500 text-white rounded-xl font-semibold hover:bg-emerald-600 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap flex items-center gap-2"
                >
                  <i className="ri-download-line"></i>
                  Download Free Sample
                </button>
              </div>
            </div>

            {/* All Worksheets Grid */}
            <div className="grid md:grid-cols-2 gap-6">
              {worksheets.map((worksheet) => (
                <div
                  key={worksheet.id}
                  className={`bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border-2 ${
                    worksheet.isFree ? 'border-emerald-200' : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      {worksheet.isFree && (
                        <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs font-bold rounded-full">
                          FREE
                        </span>
                      )}
                      <span className={`px-2 py-1 text-xs font-semibold rounded-lg ${
                        worksheet.difficulty === 'Easy' ? 'bg-green-100 text-green-700' :
                        worksheet.difficulty === 'Medium' ? 'bg-amber-100 text-amber-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {worksheet.difficulty}
                      </span>
                    </div>
                    {!worksheet.isFree && (
                      <i className="ri-lock-line text-gray-400 text-xl"></i>
                    )}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {worksheet.title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4">
                    {worksheet.description}
                  </p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <i className="ri-file-text-line"></i>
                        {worksheet.pages}p
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-calculator-line"></i>
                        {worksheet.problems}
                      </span>
                    </div>
                    <button
                      onClick={() => handleWorksheetClick(worksheet)}
                      className={`px-4 py-2 rounded-lg font-semibold transition-all duration-300 whitespace-nowrap ${
                        worksheet.isFree
                          ? 'bg-emerald-500 text-white hover:bg-emerald-600'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {worksheet.isFree ? 'Download' : 'Unlock'}
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Membership Upsell Section */}
            {showMembershipUpsell && (
              <div id="membership-upsell" className="bg-gradient-to-br from-indigo-600 to-purple-600 rounded-2xl p-8 text-white shadow-2xl">
                <div className="text-center max-w-3xl mx-auto">
                  <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4 backdrop-blur-sm">
                    <i className="ri-vip-crown-line text-4xl"></i>
                  </div>
                  <h2 className="text-3xl font-bold mb-4">
                    Unlock All Grade 12 Worksheets
                  </h2>
                  <p className="text-indigo-50 text-lg mb-6">
                    Get instant access to our complete collection of Precalculus practice worksheets, covering advanced topics from trigonometry to limits
                  </p>
                  <div className="flex flex-wrap justify-center gap-4 mb-6">
                    <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-lg backdrop-blur-sm">
                      <i className="ri-check-line text-emerald-300"></i>
                      <span>160+ Practice Problems</span>
                    </div>
                    <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-lg backdrop-blur-sm">
                      <i className="ri-check-line text-emerald-300"></i>
                      <span>Detailed Solutions</span>
                    </div>
                    <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-lg backdrop-blur-sm">
                      <i className="ri-check-line text-emerald-300"></i>
                      <span>Calculus Prep</span>
                    </div>
                  </div>
                  <button
                    onClick={handleBrowseAllClick}
                    className="px-8 py-4 bg-white text-indigo-600 rounded-xl font-bold text-lg hover:bg-indigo-50 transition-all duration-300 shadow-xl hover:shadow-2xl whitespace-nowrap"
                  >
                    View All Worksheets & Plans
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Standards Tab */}
        {activeTab === 'standards' && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200">
              <h3 className="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-teal-600 to-emerald-600 rounded-lg flex items-center justify-center">
                  <i className="ri-checkbox-circle-line text-xl text-white"></i>
                </div>
                Common Core & CCSS-M — Precalculus Standards
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <h4 className="font-bold text-teal-600 mb-3">Functions & Analysis</h4>
                  <div className="space-y-2 text-sm text-slate-700">
                    <p>• HSF-BF.A.1c — Compose functions</p>
                    <p>• HSF-BF.B.4 — Find inverse functions</p>
                    <p>• HSF-IF.B.4 — Interpret key features of functions</p>
                    <p>• HSF-IF.C.7 — Graph functions and identify features</p>
                    <p>• HSF-IF.C.9 — Compare properties of two functions</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <h4 className="font-bold text-emerald-600 mb-3">Trigonometry</h4>
                  <div className="space-y-2 text-sm text-slate-700">
                    <p>• HSF-TF.A.1 — Radian measure and arc length</p>
                    <p>• HSF-TF.A.2 — Unit circle and trig values</p>
                    <p>• HSF-TF.B.5 — Model periodic phenomena</p>
                    <p>• HSF-TF.C.8 — Pythagorean identity</p>
                    <p>• HSF-TF.C.9 — Sum/difference identities</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <h4 className="font-bold text-teal-600 mb-3">Conic Sections & Geometry</h4>
                  <div className="space-y-2 text-sm text-slate-700">
                    <p>• HSG-GPE.A.1 — Derive equation of a circle</p>
                    <p>• HSG-GPE.A.2 — Derive equation of a parabola</p>
                    <p>• HSG-GPE.A.3 — Derive equations of ellipses/hyperbolas</p>
                    <p>• HSN-VM.A.1 — Magnitude and direction of vectors</p>
                    <p>• HSN-VM.B.4 — Add and subtract vectors</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <h4 className="font-bold text-emerald-600 mb-3">Sequences, Series & Limits</h4>
                  <div className="space-y-2 text-sm text-slate-700">
                    <p>• HSF-BF.A.2 — Arithmetic & geometric sequences</p>
                    <p>• HSA-SSE.B.4 — Finite geometric series formula</p>
                    <p>• HSA-APR.C.5 — Binomial Theorem</p>
                    <p>• (+) Limits of sequences and functions</p>
                    <p>• (+) Introduction to derivatives via difference quotient</p>
                  </div>
                </div>
              </div>
            </div>

            {/* State Alignment */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200">
              <h3 className="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-emerald-600 to-teal-600 rounded-lg flex items-center justify-center">
                  <i className="ri-map-pin-line text-xl text-white"></i>
                </div>
                State Standards Alignment
              </h3>
              <div className="grid md:grid-cols-3 gap-6">
                {[
                  { flag: '🗽', state: 'New York', color: 'from-blue-50 to-indigo-50 border-blue-100', textColor: 'text-blue-700', desc: 'Aligned with NY Regents Precalculus curriculum — all topics covered including limits and vectors' },
                  { flag: '⭐', state: 'Texas', color: 'from-red-50 to-orange-50 border-red-100', textColor: 'text-red-700', desc: 'TEKS Precalculus standards — parametric equations, polar coordinates, conic sections' },
                  { flag: '🌻', state: 'Ohio', color: 'from-emerald-50 to-teal-50 border-emerald-100', textColor: 'text-emerald-700', desc: 'Ohio Learning Standards — functions, modeling, trigonometry, data analysis' },
                  { flag: '🏛️', state: 'Virginia', color: 'from-slate-50 to-gray-50 border-slate-100', textColor: 'text-slate-700', desc: 'Virginia SOL Precalculus — analytic geometry, sequences, trig applications' },
                  { flag: '🔔', state: 'Pennsylvania', color: 'from-yellow-50 to-amber-50 border-yellow-100', textColor: 'text-yellow-700', desc: 'PA Keystone standards — algebra, functions, trigonometry, analytic geometry' },
                  { flag: '🎓', state: 'AP Calculus Ready', color: 'from-teal-50 to-emerald-50 border-teal-100', textColor: 'text-teal-700', desc: 'All topics align with AP Calculus AB/BC prerequisites — limits, functions, trig' },
                ].map((item, i) => (
                  <div key={i} className={`bg-gradient-to-br ${item.color} rounded-xl p-5 border`}>
                    <h4 className={`font-bold ${item.textColor} mb-2`}>{item.flag} {item.state}</h4>
                    <p className="text-sm text-slate-600 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* College Readiness */}
            <div className="bg-gradient-to-br from-teal-600 to-emerald-600 rounded-2xl p-8 text-white shadow-xl">
              <h3 className="text-2xl font-bold mb-4 flex items-center gap-3">
                <i className="ri-trophy-line text-3xl"></i>
                College & Career Readiness
              </h3>
              <p className="text-teal-50 leading-relaxed">
                Precalculus is the gateway to AP Calculus, college mathematics, engineering, physics, computer science, and data science. Mastering these concepts — especially limits, trigonometric identities, and function analysis — gives you a decisive advantage in every STEM field. You're not just preparing for a test; you're building the mathematical foundation for your future.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
